// The repo's ONE keyset-cursor codec (BL-1556). Lifted out of
// `@auctogon/core-bidder/schemas/lot-cursor` and generalised over the sort key
// so admin and bidder surfaces share one implementation rather than three.
//
// A keyset cursor means "resume strictly after this row", expressed as the sort
// column's value for that row plus its id as the tie-break. Unlike an offset it
// cannot skip or duplicate rows when the set shifts underneath the reader —
// which is the entire reason it is used here. The pending-registration queue is
// the sharpest case: approving a registration REMOVES it from the set, so
// `OFFSET` paging forward silently steps over whoever moved up into the vacated
// slot, and that person is then never reviewed (BL-1556 AC 7).
//
// The cursor carries the sort key it was minted under. A cursor is an opaque
// string a client round-trips, so if the sort changes while one is held, seeking
// the old column against the new ordering would return an arbitrary slice.
// Instead the cursor is REJECTED and the caller starts at page one — correct,
// and visible rather than silently wrong.
//
// CONSOLIDATION NOTE (ADR 044 — Must Consolidate). Two codecs existed before
// this file: `lot-cursor.ts` (base64 JSON, the shape kept here) and a private
// `<createdAt>|<id>` pair inside `actions-log-service.ts`. Both are now callers,
// neither is an alternative implementation — the actions-log pair is deleted.
// If you are about to write a third, don't: bind these to your sort-key union
// instead.
//
// BL-2107: when the sort value is something the client must not read (a
// realtime-projected price, close time or lead flag), seal the position with
// `sealed-keyset-cursor.ts` — it wraps THIS codec rather than replacing it. The
// bidder's Watchlist / My Bids cursors are sealed that way.

import { isTimestamptzText, isUuid } from '../isomorphic/validation/input-guards';

/** A decoded cursor position: where the previous page stopped. */
export interface KeysetCursorPosition<TSort extends string> {
    /** The sort this cursor was minted under. A mismatch invalidates it. */
    sort: TSort;
    /**
     * The sort column's value on the last row of the previous page, as text.
     * `null` is meaningful — a row can genuinely sort on NULL (a lot with no
     * close time), and a cursor that couldn't represent that would break paging
     * exactly at the boundary where those rows start.
     */
    value: string | null;
    /** That row's id — the tie-break that makes the ordering total. */
    id: string;
}

/**
 * Opt-in column guards for a decoded position (BUG-1827). Each names the type
 * of the column the caller's SQL compares against, so a forged cursor whose
 * value or id cannot be cast is refused here rather than at Postgres.
 */
export interface KeysetCursorGuards {
    /** The sort column is a `timestamptz`: `value` must be non-null and cast cleanly. */
    value?: 'timestamptz';
    /** The tie-break column is a `uuid`: `id` must be UUID-shaped. */
    id?: 'uuid';
}

/** Base64 that works on both server (Buffer) and client (btoa) runtimes. */
function toBase64(input: string): string {
    if (typeof Buffer !== 'undefined') return Buffer.from(input, 'utf8').toString('base64');
    return btoa(input);
}

function fromBase64(input: string): string | null {
    try {
        if (typeof Buffer !== 'undefined') return Buffer.from(input, 'base64').toString('utf8');
        return atob(input);
    } catch {
        return null;
    }
}

/**
 * Encode a page position into an opaque cursor.
 *
 * Opaque on purpose: the shape is ours to change, and a client that
 * hand-crafted one would be coupling to an internal ordering detail.
 *
 * @param position - where the page stopped.
 * @returns the cursor string to hand back to the client.
 * @sideEffect none — pure.
 */
export function encodeKeysetCursor<TSort extends string>(
    position: KeysetCursorPosition<TSort>,
): string {
    return toBase64(JSON.stringify({ k: position.sort, v: position.value, i: position.id }));
}

/**
 * Decode a client-supplied cursor, validating it against the CURRENT sort.
 *
 * Returns `null` — never throws — for anything unusable: malformed base64,
 * non-JSON, a missing field, or a cursor minted under a different sort. The
 * caller treats `null` as "start from the beginning", so a stale or tampered
 * cursor costs the reader a jump to page one rather than a 500.
 *
 * By default the check is shape-only: `id` is a non-empty string and `value` is
 * not parsed, because the codec does not know the caller's column types. A
 * caller whose SQL casts the value or compares the id to a uuid column passes
 * `guards` naming those types (BUG-1827); a well-shaped cursor that fails them
 * decodes to `null` too, so it costs page one rather than a cast-error 500.
 * Every keyset over a `timestamptz` + `uuid` pair declares both.
 *
 * @param cursor - the opaque string from the request.
 * @param currentSort - the sort this request is running.
 * @param guards - the column types `value` and `id` are compared against, if any.
 * @returns the position, or `null` if it cannot be honoured.
 * @sideEffect none — pure.
 */
export function decodeKeysetCursor<TSort extends string>(
    cursor: string | null | undefined,
    currentSort: TSort,
    guards: KeysetCursorGuards = {},
): KeysetCursorPosition<TSort> | null {
    if (!cursor) return null;

    const json = fromBase64(cursor);
    if (json == null) return null;

    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }

    if (typeof parsed !== 'object' || parsed === null) return null;
    const { k, v, i } = parsed as { k?: unknown; v?: unknown; i?: unknown };

    if (typeof k !== 'string' || typeof i !== 'string' || i.length === 0) return null;
    // The sort guard. Not a validation nicety — honouring a mismatched cursor
    // seeks the wrong column and returns an arbitrary slice.
    if (k !== currentSort) return null;
    if (v !== null && typeof v !== 'string') return null;
    if (guards.value === 'timestamptz' && (v === null || !isTimestamptzText(v))) return null;
    if (guards.id === 'uuid' && !isUuid(i)) return null;

    return { sort: k as TSort, value: v, id: i };
}
